import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dataApi } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

export function MediaAnalysisPanel({
  familyId,
  mediaId,
}: {
  familyId: string;
  mediaId: string;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [busyFace, setBusyFace] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const analysis = useQuery({
    queryKey: ['media-analysis', familyId, mediaId],
    queryFn: () => dataApi.mediaAnalysis(familyId, mediaId),
  });

  async function confirm(faceId: string, personId: string) {
    setBusyFace(faceId);
    setError(null);
    try {
      await dataApi.confirmMediaFace(familyId, mediaId, faceId, personId);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['media-analysis', familyId, mediaId],
        }),
        queryClient.invalidateQueries({ queryKey: ['person'] }),
        queryClient.invalidateQueries({ queryKey: ['tree', familyId] }),
      ]);
    } catch (err) {
      setError(err instanceof Error ? t(err.message) : t('Не удалось сохранить'));
    } finally {
      setBusyFace(null);
    }
  }

  async function reject(faceId: string) {
    setBusyFace(faceId);
    setError(null);
    try {
      await dataApi.rejectMediaFace(familyId, mediaId, faceId);
      await queryClient.invalidateQueries({
        queryKey: ['media-analysis', familyId, mediaId],
      });
    } catch (err) {
      setError(err instanceof Error ? t(err.message) : t('Не удалось сохранить'));
    } finally {
      setBusyFace(null);
    }
  }

  if (analysis.isLoading) {
    return <p className="text-xs text-white/60">{t('Анализируем фото…')}</p>;
  }
  if (analysis.isError) {
    return (
      <p className="text-xs text-white/60">
        {t('Анализ фото сейчас недоступен.')}
      </p>
    );
  }

  const faces = analysis.data?.faces ?? [];
  const suggested = faces.filter(
    (face) => face.status === 'SUGGESTED' && face.suggestions.length > 0,
  );
  const confirmed = faces.filter((face) => face.status === 'CONFIRMED');
  const ocrText = analysis.data?.ocrText?.trim() ?? '';
  if (suggested.length === 0 && confirmed.length === 0 && !ocrText) return null;

  return (
    <div className="space-y-3">
      {suggested.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-white/50">
            {t('Возможные люди на фото')}
          </p>
          {suggested.map((face, faceIndex) => (
            <div key={face.id} className="rounded-lg bg-white/10 p-2">
              <p className="mb-1 text-xs text-white/60">
                {t('Лицо {index}', { index: faceIndex + 1 })}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {face.suggestions.map((suggestion) => (
                  <span key={suggestion.personId} className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => void confirm(face.id, suggestion.personId)}
                      disabled={busyFace === face.id}
                      className="rounded-full bg-white/15 px-2 py-1 text-xs text-white hover:bg-white/25 disabled:opacity-50"
                    >
                      {suggestion.name} · {Math.round(suggestion.confidence * 100)}%
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => void reject(face.id)}
                  disabled={busyFace === face.id}
                  className="rounded-full px-2 py-1 text-xs text-white/60 underline hover:text-white disabled:opacity-50"
                >
                  {t('Не этот человек')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmed.length > 0 && (
        <p className="text-xs text-white/70">
          {t('Отмечено на фото: {names}', {
            names: confirmed
              .map((face) => face.suggestions[0]?.name ?? '')
              .filter(Boolean)
              .join(', '),
          })}
        </p>
      )}

      {ocrText && (
        <details className="rounded-lg bg-white/10 p-2 text-xs text-white/70">
          <summary className="cursor-pointer text-white/80">
            {t('Распознанный текст')}
          </summary>
          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words font-sans text-xs">
            {ocrText}
          </pre>
        </details>
      )}

      {error && <p className="text-xs text-red-300">{error}</p>}
    </div>
  );
}
